/* eslint-disable no-self-assign */
import React, { useEffect, lazy, Suspense } from "react";
import { connect, useSelector } from "react-redux";
import { func, bool, number, arrayOf, object, string } from "prop-types";
import Notifications from "containers/Notifications";
import ToolBar from "containers/ToolBar";
import TopBar from "containers/TopBar";
import Reporter from "containers/Reporter";
import ImageAnnotation from "containers/ImageAnnotation";
import InputDataPicker from "components/InputDataPicker";
import SnapshotNotification from "components/SnapshotNotification";
import LoadingDialog from "containers/LoadingDialog";
import { selectLocale, selectReporterTab } from "redux/app/selectors";
import { adminApp, env } from "api/constants";
import { selectLabelsForDropDown } from "redux/points/selectors";
import { loadRepo, loadRepoError } from "redux/app/actions";
import { getCookie, getParameterByName } from "helpers";
import packageJson from "../package.json";
import { isProduction } from "api/constants";
import { useDispatch } from "react-redux";
import style from "./style.module.scss";
import { jumpToStep } from "redux/tourGuide/actions";
import { endCounting, startCounting } from "redux/timing/actions";
import { selectIsCountingTime, selectRemainTime } from "redux/timing/selectors";
import LimitTimeModal from "components/LimitTimeModal";
import { selectModalObject } from "redux/canvas/selectors";
import { setModalItem } from "redux/canvas/actions";
import { initialState as canvasInitialState } from "redux/canvas/reducer";
import AppLocale from "./lang";
import { IntlProvider } from "react-intl";
import moment from "moment";
require("moment/locale/ja");

const GeneralModal = lazy(() => import("components/Modal/Modal-v1"));

function App({
  locale,
  loadRepo,
  loadRepoError,
  jumpToStep,
  stepIndex,
  isTourOpen,
  labels,
  remainTime,
  modalItem,
  setModalItem,
  reporterTab,
}) {
  const dispatch = useDispatch();
  const isCountingTime = useSelector(selectIsCountingTime);

  const refreshCacheAndReload = () => {
    if (caches) {
      // Service worker cache should be cleared with caches.delete()
      caches.keys().then((names) => {
        for (const name of names) {
          caches.delete(name);
        }
      });
    }
    // // delete browser cache and hard reload
    window.location.href = window.location.href;
  };

  const handleListenFocusEvent = () => {
    const tokenInCookie = getCookie("Bearer");
    if (tokenInCookie && !isCountingTime && isProduction) {
      dispatch(startCounting());
    }
  };

  const handleListenFocusOutEvent = () => {
    isProduction && dispatch(endCounting());
  };

  useEffect(() => {
    if (isProduction) {
      fetch("/meta.json")
        .then((response) => response.json())
        .then((meta) => {
          if (meta.buildDate !== packageJson.buildDate) {
            refreshCacheAndReload();
          }
        });
    }
    const isTourOpen = getParameterByName("isTourOpen");
    if (!isCountingTime && !isTourOpen && isProduction) {
      dispatch(startCounting());
    }

    window.addEventListener("focus", handleListenFocusEvent);
    window.addEventListener("blur", handleListenFocusOutEvent);

    return () => {
      window.removeEventListener("focus", handleListenFocusEvent);
      window.removeEventListener("blur", handleListenFocusOutEvent);
    };
  }, []);

  useEffect(() => {
    // TODO: find a solution to get token
    const tokenInCookie = getCookie("Bearer");
    if (tokenInCookie) {
      loadRepo({ tokenInCookie });
    } else {
      loadRepoError("Error when fetching the repository.");
      if (env !== "development") {
        window.location.href = adminApp;
      }
    }
  }, [loadRepo, loadRepoError]);

  useEffect(() => {
    const isTourOpenParams = getParameterByName("isTourOpen");
    // const isOldInImage = localStorage.getItem("isOldInImage");
    if (
      labels.length > 0 &&
      isTourOpenParams
      // && !isOldInImage
    ) {
      jumpToStep &&
        jumpToStep({
          stepIndex: stepIndex + 1,
          isOpen: !isTourOpen,
        });
      // localStorage.setItem("isOldInImage", true);
    }
  }, [labels.length]);

  const handleToogleGeneralModal = () => {
    setModalItem(canvasInitialState.modalItem);
  };
  const handleSubmitAction = () => {
    const { fnAction, payload: modalPayload } = modalItem;
    dispatch(fnAction(modalPayload));
  };

  const handleSubmitFn = () => {
    const { fn, payload: modalPayload } = modalItem;
    fn(modalPayload);
  };

  const currentAppLocale = AppLocale[locale];
  const localStorageAppLocale = localStorage.getItem("currentLanguage");

  if (currentAppLocale && moment.locale() !== currentAppLocale.locale) {
    moment.locale(currentAppLocale.locale);
  }

  if (
    currentAppLocale &&
    !currentAppLocale.locale.includes(localStorageAppLocale)
  ) {
    localStorage.setItem("currentLanguage", locale);
  }

  return (
    <IntlProvider
      locale={currentAppLocale.locale}
      messages={currentAppLocale.messages}
    >
      <div>
        <TopBar />
        <Notifications />
        <div className={style.container} id="container">
          <ToolBar reporterTab={reporterTab} />
          <InputDataPicker />
          <ImageAnnotation />
          <Reporter reporterTab={reporterTab} />
        </div>
        <LoadingDialog />
        <SnapshotNotification />
        <LimitTimeModal open={remainTime === 0} />
        {modalItem.id && (
          <Suspense fallback={<></>}>
            <GeneralModal
              title={modalItem.title}
              titleId={modalItem.titleId}
              content={modalItem.content}
              contentId={modalItem.contentId}
              isOpen={modalItem.id}
              toggle={handleToogleGeneralModal}
              handleSubmit={modalItem.fn ? handleSubmitFn : handleSubmitAction}
            />
          </Suspense>
        )}
      </div>
    </IntlProvider>
  );
}

App.propTypes = {
  locale: string,
  loadRepo: func,
  loadRepoError: func,
  isTourOpen: bool,
  stepIndex: number,
  jumpToStep: func,
  labels: arrayOf(object),
  remainTime: number,
  modalItem: object,
  setModalItem: func,
  reporterTab: string,
};

const mapPropsToState = (state) => {
  const { tourGuide } = state;
  const { isOpen: isTourOpen, stepIndex } = tourGuide;

  return {
    locale: selectLocale(state),
    labels: selectLabelsForDropDown()(state),
    isTourOpen,
    stepIndex,
    remainTime: selectRemainTime(state),
    modalItem: selectModalObject(state),
    reporterTab: selectReporterTab(state),
  };
};

export default connect(mapPropsToState, {
  loadRepo,
  loadRepoError,
  jumpToStep,
  setModalItem,
})(App);
